import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  TrendingUp, TrendingDown, Minus, Euro, ShoppingCart, Users, Clock,
  Package, BarChart3, PieChart, RefreshCw, ArrowUp, ArrowDown,
  Wallet, Target, Calendar, Star, Table2
} from 'lucide-react';
import { toast } from 'sonner';
import { DateRangePicker, rangeQuery } from './common/DateRangePicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Metric Card Component
const MetricCard = ({ title, value, subtitle, trend, icon: Icon, color = '#B78C3C' }) => (
  <Card className="relative overflow-hidden">
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">{title}</p>
          <p className="text-2xl font-bold mt-1" style={{ color }}>{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon size={24} style={{ color }} />
        </div>
      </div>
      {trend !== undefined && trend !== null && (
        <div className={`flex items-center gap-1 mt-3 text-sm ${
          trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-500'
        }`}>
          {trend > 0 ? <TrendingUp size={14} /> : trend < 0 ? <TrendingDown size={14} /> : <Minus size={14} />}
          <span>{trend > 0 ? '+' : ''}{trend.toFixed(1)}% vs período anterior</span>
        </div>
      )}
    </CardContent>
  </Card>
);

// Bar Chart Component (simple horizontal bars)
const HorizontalBarChart = ({ data, valueKey = 'revenue', labelKey = 'name', color = '#B78C3C', suffix = '€' }) => {
  if (!data || data.length === 0) return <p className="text-gray-400 text-sm">Sin datos</p>;
  
  const maxValue = Math.max(...data.map(d => d[valueKey] || 0));
  
  return (
    <div className="space-y-2">
      {data.slice(0, 8).map((item, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <div className="w-32 text-sm text-gray-600 truncate">{item[labelKey]}</div>
          <div className="flex-1 relative h-6 bg-gray-100 rounded">
            <div 
              className="absolute inset-y-0 left-0 rounded transition-all duration-500"
              style={{ 
                width: `${maxValue > 0 ? (item[valueKey] / maxValue) * 100 : 0}%`,
                backgroundColor: color,
                minWidth: item[valueKey] > 0 ? '4px' : '0'
              }}
            />
          </div>
          <div className="w-20 text-sm text-right font-medium">
            {typeof item[valueKey] === 'number' ? (Number.isInteger(item[valueKey]) ? item[valueKey] : item[valueKey].toFixed(2)) : item[valueKey]}{suffix}
          </div>
        </div>
      ))}
    </div>
  );
};

// Hourly Chart Component
const HourlyChart = ({ data, color = '#B78C3C' }) => {
  if (!data || data.length === 0) return <p className="text-gray-400 text-sm">Sin datos</p>;
  
  const maxRevenue = Math.max(...data.map(d => d.revenue || 0));
  
  return (
    <div className="flex items-end justify-between h-32 gap-1">
      {data.map((hour, idx) => (
        <div key={idx} className="flex-1 flex flex-col items-center">
          <div 
            className="w-full rounded-t transition-all duration-300"
            style={{ 
              height: `${maxRevenue > 0 ? (hour.revenue / maxRevenue) * 100 : 0}%`,
              backgroundColor: hour.revenue > 0 ? color : '#E5E7EB',
              minHeight: '2px'
            }}
            title={`${hour.hour}:00 - ${hour.revenue?.toFixed(2)}€`}
          />
          <span className="text-xs text-gray-400 mt-1">
            {hour.hour % 4 === 0 ? `${hour.hour}h` : ''}
          </span>
        </div>
      ))}
    </div>
  );
};

// Daily Chart Component
const DailyChart = ({ data, color = '#B78C3C' }) => {
  if (!data || data.length === 0) return <p className="text-gray-400 text-sm">Sin datos</p>;
  
  const maxRevenue = Math.max(...data.map(d => d.revenue || 0));
  const minRevenue = Math.min(...data.map(d => d.revenue || 0));
  
  return (
    <div className="h-40">
      <svg viewBox="0 0 100 40" className="w-full h-full" preserveAspectRatio="none">
        {/* Area fill */}
        <path
          d={`M0,40 ${data.map((d, i) => {
            const x = (i / (data.length - 1)) * 100;
            const y = 40 - ((d.revenue - minRevenue) / (maxRevenue - minRevenue || 1)) * 35;
            return `L${x},${y}`;
          }).join(' ')} L100,40 Z`}
          fill={`${color}20`}
        />
        {/* Line */}
        <path
          d={`M${data.map((d, i) => {
            const x = (i / (data.length - 1)) * 100;
            const y = 40 - ((d.revenue - minRevenue) / (maxRevenue - minRevenue || 1)) * 35;
            return `${i === 0 ? '' : 'L'}${x},${y}`;
          }).join(' ')}`}
          fill="none"
          stroke={color}
          strokeWidth="0.5"
        />
      </svg>
      <div className="flex justify-between text-xs text-gray-400 mt-1">
        <span>{data[0]?.date?.slice(5)}</span>
        <span>{data[data.length - 1]?.date?.slice(5)}</span>
      </div>
    </div>
  );
};

const AdvancedAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('mes');
  const [range, setRange] = useState(() => {
    const n = new Date();
    const p = (x) => String(x).padStart(2, '0');
    return { from: `${n.getFullYear()}-${p(n.getMonth() + 1)}-01`, to: `${n.getFullYear()}-${p(n.getMonth() + 1)}-${p(n.getDate())}` };
  });
  const rangeQs = periodo === 'rango' && (range.from || range.to) ? rangeQuery(range) : '';

  const fetchData = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('admin_token');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics/dashboard?periodo=${periodo === 'rango' ? 'mes' : periodo}&${rangeQs}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        toast.error('Error al cargar analytics');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  }, [periodo, rangeQs]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="animate-spin text-gray-400" size={40} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20 text-gray-500">
        No hay datos disponibles
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard Avanzado</h1>
          <p className="text-sm text-gray-500">Análisis completo del negocio</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={periodo} onValueChange={setPeriodo}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="hoy">Hoy</SelectItem>
              <SelectItem value="semana">Semana</SelectItem>
              <SelectItem value="mes">Mes</SelectItem>
              <SelectItem value="año">Año</SelectItem>
              <SelectItem value="rango">Fechas…</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw size={16} />
          </Button>
        </div>
      </div>

      {periodo === 'rango' && (
        <div className="bg-white rounded-xl border shadow-sm p-3">
          <DateRangePicker value={range} onChange={setRange} idPrefix="analytics-range" />
        </div>
      )}

      {/* Main Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          title="Ingresos"
          value={`${data.ventas.ingresos.toFixed(2)}€`}
          trend={data.ventas.ingresos_trend}
          icon={Euro}
          color="#16A34A"
        />
        <MetricCard
          title="Pedidos"
          value={data.ventas.pedidos}
          trend={data.ventas.pedidos_trend}
          icon={ShoppingCart}
          color="#2563EB"
        />
        <MetricCard
          title="Ticket Medio"
          value={`${data.ventas.ticket_medio.toFixed(2)}€`}
          trend={data.ventas.ticket_medio_trend}
          icon={Target}
          color="#7C3AED"
        />
        <MetricCard
          title="Beneficio Neto"
          value={`${data.rentabilidad.beneficio_neto.toFixed(2)}€`}
          trend={data.rentabilidad.beneficio_trend}
          icon={Wallet}
          color={data.rentabilidad.beneficio_neto >= 0 ? '#16A34A' : '#DC2626'}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Sales Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 size={20} className="text-blue-600" />
              Evolución de Ventas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <DailyChart data={data.ventas.daily_sales} color="#2563EB" />
          </CardContent>
        </Card>

        {/* Hourly Distribution */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock size={20} className="text-purple-600" />
              Distribución por Horas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <HourlyChart data={data.clientes.hourly_distribution} color="#7C3AED" />
          </CardContent>
        </Card>
      </div>

      {/* Products Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Products */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Star size={20} className="text-amber-500" />
              Más Vendidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <HorizontalBarChart 
              data={data.productos.top_vendidos} 
              valueKey="quantity"
              color="#F59E0B"
              suffix=" uds"
            />
          </CardContent>
        </Card>

        {/* Top Categories */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Package size={20} className="text-cyan-600" />
              Categorías Top
            </CardTitle>
          </CardHeader>
          <CardContent>
            <HorizontalBarChart 
              data={data.productos.top_categorias} 
              labelKey="name"
              color="#0891B2"
            />
          </CardContent>
        </Card>

        {/* Most Profitable */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp size={20} className="text-green-600" />
              Más Rentables
            </CardTitle>
          </CardHeader>
          <CardContent>
            <HorizontalBarChart 
              data={data.productos.mas_rentables} 
              valueKey="margin"
              color="#16A34A"
            />
          </CardContent>
        </Card>
      </div>

      {/* Customers & Tables Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Peak Hours */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock size={20} className="text-orange-500" />
              Horas Punta
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.clientes.horas_punta.map((hour, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="font-mono">{hour.hour}:00</Badge>
                    <span className="text-sm text-gray-600">{hour.orders} pedidos</span>
                  </div>
                  <span className="font-bold text-gray-900">{hour.revenue.toFixed(2)}€</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Tables */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Table2 size={20} className="text-indigo-500" />
              Mesas Más Activas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.clientes.mesas_top.slice(0, 5).map((mesa, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge className="bg-indigo-100 text-indigo-700">Mesa {mesa.mesa}</Badge>
                    <span className="text-sm text-gray-600">{mesa.visits} visitas</span>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">{mesa.revenue.toFixed(2)}€</p>
                    <p className="text-xs text-gray-500">~{mesa.avg_ticket.toFixed(2)}€/visita</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Profitability Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Expenses Summary */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart size={20} className="text-red-500" />
              Gastos por Categoría
            </CardTitle>
          </CardHeader>
          <CardContent>
            <HorizontalBarChart 
              data={data.rentabilidad.gastos_por_categoria} 
              labelKey="category"
              valueKey="amount"
              color="#DC2626"
            />
          </CardContent>
        </Card>

        {/* Profitability Summary */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Resumen Financiero</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Ingresos</span>
              <span className="font-bold text-green-600">+{data.ventas.ingresos.toFixed(2)}€</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Gastos</span>
              <span className="font-bold text-red-600">-{data.rentabilidad.gastos_total.toFixed(2)}€</span>
            </div>
            <hr />
            <div className="flex justify-between items-center">
              <span className="font-medium">Beneficio Neto</span>
              <span className={`font-bold text-xl ${data.rentabilidad.beneficio_neto >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {data.rentabilidad.beneficio_neto >= 0 ? '+' : ''}{data.rentabilidad.beneficio_neto.toFixed(2)}€
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Margen</span>
              <Badge className={data.rentabilidad.margen_beneficio >= 20 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                {data.rentabilidad.margen_beneficio.toFixed(1)}%
              </Badge>
            </div>
            
            {/* Trend Indicator */}
            <div className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${
              data.tendencias.direccion === 'up' ? 'bg-green-50' :
              data.tendencias.direccion === 'down' ? 'bg-red-50' : 'bg-gray-50'
            }`}>
              {data.tendencias.direccion === 'up' ? (
                <ArrowUp className="text-green-600" size={20} />
              ) : data.tendencias.direccion === 'down' ? (
                <ArrowDown className="text-red-600" size={20} />
              ) : (
                <Minus className="text-gray-500" size={20} />
              )}
              <div>
                <p className="text-sm font-medium">
                  {data.tendencias.direccion === 'up' ? 'Tendencia alcista' :
                   data.tendencias.direccion === 'down' ? 'Tendencia bajista' : 'Estable'}
                </p>
                <p className="text-xs text-gray-500">
                  {data.tendencias.momentum > 0 ? '+' : ''}{data.tendencias.momentum.toFixed(1)}% momentum
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 text-center">
            <Users className="mx-auto text-blue-600 mb-2" size={24} />
            <p className="text-2xl font-bold text-blue-700">{data.clientes.duracion_media_min} min</p>
            <p className="text-xs text-blue-600">Estancia media</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4 text-center">
            <Package className="mx-auto text-purple-600 mb-2" size={24} />
            <p className="text-2xl font-bold text-purple-700">{data.productos.top_vendidos.length}</p>
            <p className="text-xs text-purple-600">Productos activos</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4 text-center">
            <Target className="mx-auto text-green-600 mb-2" size={24} />
            <p className="text-2xl font-bold text-green-700">{data.ventas.ticket_medio.toFixed(2)}€</p>
            <p className="text-xs text-green-600">Ticket medio</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 text-center">
            <Calendar className="mx-auto text-amber-600 mb-2" size={24} />
            <p className="text-2xl font-bold text-amber-700">
              {data.clientes.horas_punta[0]?.hour || '-'}:00
            </p>
            <p className="text-xs text-amber-600">Hora punta</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdvancedAnalytics;
